/**
 * Hyperliquid Transfers Adapter
 *
 * Implements PerpsTransfersAdapter for fund movement on Hyperliquid:
 * - withdraw3: Bridge USDC from HL L1 back to Arbitrum
 * - usdSend: Transfer USDC to another HL user
 * - usdClassTransfer: Move funds between Spot ↔ Perp
 *
 * All operations require the main wallet signature (not agent wallet).
 */

import {
  buildWithdraw3Action,
  buildUsdSendAction,
  buildUsdClassTransferAction,
  HL_EIP712_DOMAIN,
  HL_MAINNET_API,
  HL_TESTNET_API,
  splitSignature,
} from "../client";
import { userSignedActionEip712Types } from "@nktkas/hyperliquid/signing";
import type {
  PerpsWithdrawParams,
  PerpsTransferParams,
  PerpsClassTransferParams,
  PerpsTransferResult,
  PerpsDepositConfig,
} from "../types/account";
import type { PerpsSignTypedDataParams } from "../types/trading";
import type { PerpsTransfersAdapter } from "./types";
import type { HLSignature } from "../client";

// Bridge2 contract addresses (sends USDC to this contract = deposit to HL)
const HL_BRIDGE_MAINNET =
  "0x2Df1c51E09aECF9cacB7bc98cB1742757f163dF7" as `0x${string}`;
const HL_BRIDGE_TESTNET =
  "0x08cfc1B6b2dCF36A1480b99353A354AA8AC56f89" as `0x${string}`;

// Native USDC contract addresses on Arbitrum
const USDC_MAINNET =
  "0xaf88d065e77c8cC2239327C5EDb3A432268e5831" as `0x${string}`;
const USDC_TESTNET =
  "0x1baAbB04529D43a73232B713C0FE471f7c7334d5" as `0x${string}`;

export class HyperliquidTransfers implements PerpsTransfersAdapter {
  readonly supportsDeposit = true;
  readonly supportsWithdraw = true;
  readonly supportsTransfer = true;
  readonly supportsClassTransfer = true;

  private testnet: boolean;

  constructor(testnet = false) {
    this.testnet = testnet;
  }

  async withdraw(
    signTypedData: (params: PerpsSignTypedDataParams) => Promise<`0x${string}`>,
    params: PerpsWithdrawParams,
  ): Promise<PerpsTransferResult> {
    const isMainnet = !this.testnet;
    const action = buildWithdraw3Action(params, isMainnet);
    const types = userSignedActionEip712Types.withdraw3;

    const typedData = {
      domain: HL_EIP712_DOMAIN,
      types,
      primaryType: Object.keys(types)[0]!,
      message: action as unknown as Record<string, unknown>,
    };

    try {
      const signature = await signTypedData(
        typedData as PerpsSignTypedDataParams,
      );
      return this.submitAction(action, action.time, signature);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Signature rejected",
      };
    }
  }

  async transfer(
    signTypedData: (params: PerpsSignTypedDataParams) => Promise<`0x${string}`>,
    params: PerpsTransferParams,
  ): Promise<PerpsTransferResult> {
    const isMainnet = !this.testnet;
    const action = buildUsdSendAction(params, isMainnet);
    const types = userSignedActionEip712Types.usdSend;

    const typedData = {
      domain: HL_EIP712_DOMAIN,
      types,
      primaryType: Object.keys(types)[0]!,
      message: action as unknown as Record<string, unknown>,
    };

    try {
      const signature = await signTypedData(
        typedData as PerpsSignTypedDataParams,
      );
      return this.submitAction(action, action.time, signature);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Signature rejected",
      };
    }
  }

  async classTransfer(
    signTypedData: (params: PerpsSignTypedDataParams) => Promise<`0x${string}`>,
    params: PerpsClassTransferParams,
  ): Promise<PerpsTransferResult> {
    const isMainnet = !this.testnet;
    const action = buildUsdClassTransferAction(params, isMainnet);
    const types = userSignedActionEip712Types.usdClassTransfer;

    const typedData = {
      domain: HL_EIP712_DOMAIN,
      types,
      primaryType: Object.keys(types)[0]!,
      message: action as unknown as Record<string, unknown>,
    };

    try {
      const signature = await signTypedData(
        typedData as PerpsSignTypedDataParams,
      );
      return this.submitAction(action, action.nonce, signature);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Signature rejected",
      };
    }
  }

  getDepositInfo() {
    return {
      address: null,
      network: this.testnet ? "Arbitrum Sepolia" : "Arbitrum One",
      asset: "USDC",
    };
  }

  getDepositConfig(): PerpsDepositConfig {
    return {
      chainId: this.testnet ? 421614 : 42161,
      chainName: this.testnet ? "Arbitrum Sepolia" : "Arbitrum One",
      tokenAddress: this.testnet ? USDC_TESTNET : USDC_MAINNET,
      bridgeAddress: this.testnet ? HL_BRIDGE_TESTNET : HL_BRIDGE_MAINNET,
      tokenDecimals: 6,
      tokenSymbol: "USDC",
      minDeposit: "5",
    };
  }

  // --------------------------------------------------------------------------
  // Private helpers
  // --------------------------------------------------------------------------

  private async submitAction(
    action: Record<string, unknown>,
    nonce: number,
    rawSignature: `0x${string}`,
  ): Promise<PerpsTransferResult> {
    const sig: HLSignature = splitSignature(rawSignature);
    const baseUrl = this.testnet ? HL_TESTNET_API : HL_MAINNET_API;

    try {
      const response = await fetch(`${baseUrl}/exchange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          nonce,
          signature: sig,
        }),
      });

      if (!response.ok) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const result = (await response.json()) as {
        status?: string;
        response?: { type?: string; error?: string };
      };

      if (result.status === "ok") {
        return { success: true };
      }

      return {
        success: false,
        error: result.response?.error ?? "Transfer failed",
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }
}
